import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { settingsService } from '@/services/settingsService';
import {
  Settings as SettingsIcon,
  User,
  Bell,
  Palette,
  Building2,
  Clock,
  CalendarCheck,
  CheckCircle2,
  Shield,
  Sliders,
  LayoutDashboard,
  Users,
  Calendar,
  Stethoscope,
  UserCog,
  BarChart3,
  CreditCard,
  Loader2,
  Save,
  Phone,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, hasPermission, updateUser } = useAuth();
  const { hospitalId, activeClinicId, activeClinic, clinicFeatures, updateClinicFeatures } = useClinic();

  const canManageFeatures = hasPermission('FEATURE_SETTINGS_MANAGE');

  // Loading state
  const [loadingSettings, setLoadingSettings] = useState(true);

  // 1. Clinic Preferences State
  const [clinicPref, setClinicPref] = useState({
    operatingHours: '08:00 AM - 04:00 PM',
    roomNumbers: 'G-101, G-102',
    allowWalkIn: true,
    triageWaitAlert: true,
  });
  const [savedClinicPref, setSavedClinicPref] = useState({ ...clinicPref });
  const [savingClinicPref, setSavingClinicPref] = useState(false);

  // 2. Appointment Slot Settings State
  const [slotSettings, setSlotSettings] = useState({
    slotDuration: '30',
    advanceBookingWindow: '30',
    autoConfirm: true,
    preventPastScheduling: true,
  });
  const [savedSlotSettings, setSavedSlotSettings] = useState({ ...slotSettings });
  const [savingSlotSettings, setSavingSlotSettings] = useState(false);

  // 3. Feature Settings State
  const [featureForm, setFeatureForm] = useState(clinicFeatures || {});
  const [savingFeatures, setSavingFeatures] = useState(false);

  // 4. Notifications State
  const [notifications, setNotifications] = useState({
    smsReminders: true,
    queueAlerts: true,
    emailAlerts: true,
  });
  const [savedNotifications, setSavedNotifications] = useState({ ...notifications });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // 5. User Profile State
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
  });
  const [savedProfile, setSavedProfile] = useState({ ...profile });
  const [savingProfile, setSavingProfile] = useState(false);

  // Fetch settings from backend on mount and whenever activeClinicId changes
  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      if (!activeClinicId) return;
      try {
        setLoadingSettings(true);
        const data = await settingsService.getClinicSettings(activeClinicId, hospitalId);
        if (isMounted && data) {
          const cp = {
            operatingHours: data.operatingHours || activeClinic?.operatingHours || '08:00 AM - 04:00 PM',
            roomNumbers: data.roomNumbers || activeClinic?.roomNumbers?.join(', ') || 'G-101, G-102',
            allowWalkIn: data.allowWalkIn !== undefined ? Boolean(data.allowWalkIn) : true,
            triageWaitAlert: data.triageWaitAlert !== undefined ? Boolean(data.triageWaitAlert) : true,
          };
          setClinicPref(cp);
          setSavedClinicPref(cp);

          const ss = {
            slotDuration: String(data.slotDuration || '30'),
            advanceBookingWindow: String(data.advanceBookingWindow || '30'),
            autoConfirm: data.autoConfirm !== undefined ? Boolean(data.autoConfirm) : true,
            preventPastScheduling: data.preventPastScheduling !== undefined ? Boolean(data.preventPastScheduling) : true,
          };
          setSlotSettings(ss);
          setSavedSlotSettings(ss);

          const notif = {
            smsReminders: data.smsReminders !== undefined ? Boolean(data.smsReminders) : true,
            queueAlerts: data.queueAlerts !== undefined ? Boolean(data.queueAlerts) : true,
            emailAlerts: data.emailAlerts !== undefined ? Boolean(data.emailAlerts) : true,
          };
          setNotifications(notif);
          setSavedNotifications(notif);
        }
      } catch (err) {
        console.error('Failed to load clinic settings', err);
      } finally {
        if (isMounted) setLoadingSettings(false);
      }
    };

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [activeClinicId, hospitalId, activeClinic]);

  // Synchronize feature settings from clinic context
  useEffect(() => {
    if (clinicFeatures) {
      setFeatureForm(clinicFeatures);
    }
  }, [clinicFeatures]);

  // Synchronize user profile from auth context
  useEffect(() => {
    if (user) {
      const p = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
      };
      setProfile(p);
      setSavedProfile(p);
    }
  }, [user]);

  // Dirty state computations
  const isClinicPrefDirty = useMemo(
    () => JSON.stringify(clinicPref) !== JSON.stringify(savedClinicPref),
    [clinicPref, savedClinicPref]
  );

  const isSlotSettingsDirty = useMemo(
    () => JSON.stringify(slotSettings) !== JSON.stringify(savedSlotSettings),
    [slotSettings, savedSlotSettings]
  );

  const isFeaturesDirty = useMemo(
    () => JSON.stringify(featureForm) !== JSON.stringify(clinicFeatures || {}),
    [featureForm, clinicFeatures]
  );

  const isNotificationsDirty = useMemo(
    () => JSON.stringify(notifications) !== JSON.stringify(savedNotifications),
    [notifications, savedNotifications]
  );

  const isProfileDirty = useMemo(
    () => JSON.stringify(profile) !== JSON.stringify(savedProfile),
    [profile, savedProfile]
  );

  // Handlers for Save operations
  const handleSaveClinicPref = async () => {
    try {
      setSavingClinicPref(true);
      await settingsService.saveClinicSettings(activeClinicId, hospitalId, clinicPref);
      setSavedClinicPref({ ...clinicPref });
      toast.success(`Clinic preferences for ${activeClinic?.name || 'clinic'} saved successfully`);
    } catch (err) {
      toast.error('Failed to save clinic preferences');
    } finally {
      setSavingClinicPref(false);
    }
  };

  const handleSaveSlotSettings = async () => {
    try {
      setSavingSlotSettings(true);
      await settingsService.saveClinicSettings(activeClinicId, hospitalId, slotSettings);
      setSavedSlotSettings({ ...slotSettings });
      toast.success('Appointment slot settings saved successfully');
    } catch (err) {
      toast.error('Failed to save slot settings');
    } finally {
      setSavingSlotSettings(false);
    }
  };

  const handleSaveFeatures = async () => {
    try {
      setSavingFeatures(true);
      await updateClinicFeatures(featureForm);
      toast.success(`Feature settings for ${activeClinic?.name || 'clinic'} saved successfully`);
    } catch (err) {
      toast.error('Failed to save feature settings');
    } finally {
      setSavingFeatures(false);
    }
  };

  const handleSaveNotifications = async () => {
    try {
      setSavingNotifications(true);
      await settingsService.saveClinicSettings(activeClinicId, hospitalId, notifications);
      setSavedNotifications({ ...notifications });
      toast.success('Notification preferences saved successfully');
    } catch (err) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    try {
      setSavingProfile(true);
      await settingsService.updateProfile(user.id, profile);
      if (updateUser) {
        updateUser(profile);
      }
      setSavedProfile({ ...profile });
      toast.success('User profile updated successfully');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const featureItems = [
    {
      key: 'dashboardEnabled',
      title: 'Clinical Dashboard',
      badge: 'Overview',
      desc: 'Clinical metrics, OPD queue summaries, and daily operational dashboard.',
      icon: LayoutDashboard,
    },
    {
      key: 'patientsEnabled',
      title: 'Patients Directory',
      badge: 'Clinical',
      desc: 'Patient profiles, demographic records, and medical histories.',
      icon: Users,
    },
    {
      key: 'appointmentsEnabled',
      title: 'Appointments',
      badge: 'Operations',
      desc: 'Slot scheduling, upcoming patient bookings, and triage status.',
      icon: Calendar,
    },
    {
      key: 'consultationsEnabled',
      title: 'Consultations',
      badge: 'Clinical',
      desc: 'Doctor consultation notes, diagnoses, and digital prescriptions.',
      icon: Stethoscope,
    },
    {
      key: 'doctorsEnabled',
      title: 'Doctors Directory',
      badge: 'Staff',
      desc: 'Directory of doctors, operational specializations, and daily schedules.',
      icon: UserCog,
    },
    {
      key: 'reportsEnabled',
      title: 'Reports & Analytics',
      badge: 'Analytics',
      desc: 'Clinic revenue, patient footfall KPI analytics, and operational reports.',
      icon: BarChart3,
    },
    {
      key: 'paymentsEnabled',
      title: 'Payments & Revenue',
      badge: 'Finance',
      desc: 'Day-to-day revenue tracking, payment modes (Cash/UPI/Card), settlements, and receipt management.',
      icon: CreditCard,
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">PMS Application Settings</h1>
        <p className="text-slate-500 text-sm">
          Configure clinical workflows, appointment slot preferences, and notification defaults
        </p>
      </div>

      <Tabs defaultValue="clinic-pref" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 border border-slate-200">
          <TabsTrigger value="clinic-pref" className="text-xs data-[state=active]:bg-white font-medium" data-testid="tab-clinic-pref">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />
            Clinic Preferences
            {isClinicPrefDirty && (
              <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
            )}
          </TabsTrigger>
          <TabsTrigger value="appointments" className="text-xs data-[state=active]:bg-white font-medium" data-testid="tab-appointments">
            <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
            Appointment Slots
            {isSlotSettingsDirty && (
              <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
            )}
          </TabsTrigger>
          {canManageFeatures && (
            <TabsTrigger value="features" className="text-xs data-[state=active]:bg-white font-medium" data-testid="tab-features">
              <Sliders className="h-3.5 w-3.5 mr-1.5" />
              Feature Settings
              {isFeaturesDirty && (
                <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="text-xs data-[state=active]:bg-white font-medium" data-testid="tab-notifications">
            <Bell className="h-3.5 w-3.5 mr-1.5" />
            Notifications
            {isNotificationsDirty && (
              <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
            )}
          </TabsTrigger>
          <TabsTrigger value="profile" className="text-xs data-[state=active]:bg-white font-medium" data-testid="tab-profile">
            <User className="h-3.5 w-3.5 mr-1.5" />
            User Profile
            {isProfileDirty && (
              <span className="ml-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
            )}
          </TabsTrigger>
        </TabsList>

        {/* 1. CLINIC PREFERENCES */}
        <TabsContent value="clinic-pref" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900">
                    Active Clinic Configuration ({activeClinic?.name})
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Manage operational settings and room allocations for the current active clinic branch.
                  </CardDescription>
                </div>
                {isClinicPrefDirty && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] w-fit">
                    Unsaved Changes
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6 text-xs">
              {loadingSettings ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Operating OPD Hours</Label>
                      <Input
                        value={clinicPref.operatingHours}
                        onChange={(e) =>
                          setClinicPref((prev) => ({ ...prev, operatingHours: e.target.value }))
                        }
                        placeholder="e.g. 08:00 AM - 04:00 PM"
                        className="text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Clinical Room Numbers</Label>
                      <Input
                        value={clinicPref.roomNumbers}
                        onChange={(e) =>
                          setClinicPref((prev) => ({ ...prev, roomNumbers: e.target.value }))
                        }
                        placeholder="e.g. G-101, G-102"
                        className="text-xs"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Allow Walk-in Token Registration</p>
                      <p className="text-slate-500 text-[11px]">Reception can add walk-in patients directly into today's queue</p>
                    </div>
                    <Switch
                      checked={clinicPref.allowWalkIn}
                      onCheckedChange={(checked) =>
                        setClinicPref((prev) => ({ ...prev, allowWalkIn: checked }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Triage Wait Timer Alert</p>
                      <p className="text-slate-500 text-[11px]">Flag patients waiting over 20 minutes in orange</p>
                    </div>
                    <Switch
                      checked={clinicPref.triageWaitAlert}
                      onCheckedChange={(checked) =>
                        setClinicPref((prev) => ({ ...prev, triageWaitAlert: checked }))
                      }
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveClinicPref}
                      disabled={!isClinicPrefDirty || savingClinicPref}
                      className="bg-teal-600 hover:bg-teal-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-clinic-pref-btn"
                    >
                      {savingClinicPref ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-3.5 w-3.5 mr-1.5" /> Save Clinic Preferences
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2. APPOINTMENT SLOT SETTINGS */}
        <TabsContent value="appointments" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900">
                    Appointment Scheduling & Slot Duration
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Configure default consultation slot duration and advance booking limits.
                  </CardDescription>
                </div>
                {isSlotSettingsDirty && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] w-fit">
                    Unsaved Changes
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6 text-xs">
              {loadingSettings ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Default Slot Duration (Minutes)</Label>
                      <Select
                        value={slotSettings.slotDuration}
                        onValueChange={(val) =>
                          setSlotSettings((prev) => ({ ...prev, slotDuration: val }))
                        }
                      >
                        <SelectTrigger className="text-xs">
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="15">15 Minutes</SelectItem>
                          <SelectItem value="20">20 Minutes</SelectItem>
                          <SelectItem value="30">30 Minutes (Recommended)</SelectItem>
                          <SelectItem value="45">45 Minutes</SelectItem>
                          <SelectItem value="60">60 Minutes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-slate-700">Advance Booking Window (Days)</Label>
                      <Input
                        value={slotSettings.advanceBookingWindow}
                        onChange={(e) =>
                          setSlotSettings((prev) => ({ ...prev, advanceBookingWindow: e.target.value }))
                        }
                        type="number"
                        min="1"
                        max="180"
                        className="text-xs"
                      />
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Auto-Confirm Direct Bookings</p>
                      <p className="text-slate-500 text-[11px]">Automatically mark appointments as confirmed upon receptionist booking</p>
                    </div>
                    <Switch
                      checked={slotSettings.autoConfirm}
                      onCheckedChange={(checked) =>
                        setSlotSettings((prev) => ({ ...prev, autoConfirm: checked }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Prevent Past Scheduling</p>
                      <p className="text-slate-500 text-[11px]">Strictly disallow booking appointment time slots in the past</p>
                    </div>
                    <Switch checked={slotSettings.preventPastScheduling} disabled />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveSlotSettings}
                      disabled={!isSlotSettingsDirty || savingSlotSettings}
                      className="bg-teal-600 hover:bg-teal-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-slot-settings-btn"
                    >
                      {savingSlotSettings ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-3.5 w-3.5 mr-1.5" /> Save Slot Settings
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3. FEATURE SETTINGS */}
        {canManageFeatures && (
          <TabsContent value="features" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm font-bold text-slate-900">
                      Clinic Feature Modules ({activeClinic?.name})
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      Manage functional tabs and features enabled for this clinic. Disabled features are hidden from the sidebar.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {isFeaturesDirty && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px]">
                        Unsaved Changes
                      </Badge>
                    )}
                    <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-[11px] self-start sm:self-auto">
                      Per-Clinic Configuration
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-xs">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {featureItems.map((item) => {
                    const Icon = item.icon;
                    const isChecked = Boolean(featureForm[item.key]);
                    return (
                      <div
                        key={item.key}
                        className="flex items-start justify-between p-3.5 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors bg-white shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-md bg-teal-50 text-teal-700 mt-0.5">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900 text-xs">{item.title}</span>
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                                {item.badge}
                              </Badge>
                            </div>
                            <p className="text-slate-500 text-[11px] leading-tight max-w-[260px]">
                              {item.desc}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            setFeatureForm((prev) => ({ ...prev, [item.key]: checked }))
                          }
                          data-testid={`switch-${item.key}`}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
                  <p className="text-slate-500 text-[11px]">
                    * Disabled modules will be hidden from the sidebar and protected from direct URL access.
                  </p>
                  <Button
                    onClick={handleSaveFeatures}
                    disabled={!isFeaturesDirty || savingFeatures}
                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-9 px-4 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-features-btn"
                  >
                    {savingFeatures ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-3.5 w-3.5 mr-1.5" /> Save Feature Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* 4. NOTIFICATIONS */}
        <TabsContent value="notifications" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900">
                    Patient & Clinical Notifications
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Manage real-time sound cues, patient SMS reminders, and daily report emails.
                  </CardDescription>
                </div>
                {isNotificationsDirty && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] w-fit">
                    Unsaved Changes
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6 text-xs">
              {loadingSettings ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">SMS Appointment Reminders (+91)</p>
                      <p className="text-slate-500 text-[11px]">Send SMS alerts to patients 24 hours prior to appointment</p>
                    </div>
                    <Switch
                      checked={notifications.smsReminders}
                      onCheckedChange={(checked) =>
                        setNotifications((prev) => ({ ...prev, smsReminders: checked }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Queue Check-in Sound Alerts</p>
                      <p className="text-slate-500 text-[11px]">Play audio ping when a patient checks into doctor queue</p>
                    </div>
                    <Switch
                      checked={notifications.queueAlerts}
                      onCheckedChange={(checked) =>
                        setNotifications((prev) => ({ ...prev, queueAlerts: checked }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="font-semibold text-slate-900">Daily OPD Summary Email</p>
                      <p className="text-slate-500 text-[11px]">Send end-of-day consultation statistics report to physicians</p>
                    </div>
                    <Switch
                      checked={notifications.emailAlerts}
                      onCheckedChange={(checked) =>
                        setNotifications((prev) => ({ ...prev, emailAlerts: checked }))
                      }
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveNotifications}
                      disabled={!isNotificationsDirty || savingNotifications}
                      className="bg-teal-600 hover:bg-teal-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="save-notifications-btn"
                    >
                      {savingNotifications ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-3.5 w-3.5 mr-1.5" /> Save Notification Preferences
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 5. USER PROFILE */}
        <TabsContent value="profile" className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-bold text-slate-900">
                    Physician & Staff Profile
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Manage your personal staff credentials and phone contact details.
                  </CardDescription>
                </div>
                {isProfileDirty && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-[10px] w-fit">
                    Unsaved Changes
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6 text-xs">
              <div className="flex items-center gap-5">
                <Avatar className="h-16 w-16 ring-2 ring-teal-500/20">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-teal-700 text-white text-lg font-bold">
                    {profile.firstName?.[0] || user?.firstName?.[0] || 'U'}{profile.lastName?.[0] || user?.lastName?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-bold text-sm text-slate-900">{profile.firstName || user?.firstName} {profile.lastName || user?.lastName}</p>
                  <p className="text-slate-500">{user?.email}</p>
                  <p className="text-teal-700 font-semibold uppercase text-[10px] mt-1">
                    Designation: {user?.commonRole || user?.designation || user?.role || 'Staff'}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">First Name</Label>
                  <Input
                    value={profile.firstName}
                    onChange={(e) => setProfile((prev) => ({ ...prev, firstName: e.target.value }))}
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Last Name</Label>
                  <Input
                    value={profile.lastName}
                    onChange={(e) => setProfile((prev) => ({ ...prev, lastName: e.target.value }))}
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Contact Phone</Label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="e.g. 9876543210"
                    className="text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Email Address</Label>
                  <Input value={user?.email || ''} disabled className="text-xs bg-slate-50 cursor-not-allowed" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">System Role (Assigned by Platform)</Label>
                  <Input value={(user?.role || 'STAFF').toUpperCase()} disabled className="text-xs bg-slate-50 font-mono cursor-not-allowed" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Assigned Clinic Units</Label>
                  <Input
                    value={Array.isArray(user?.assignedClinics) ? user.assignedClinics.join(', ') : (activeClinic?.name || '101')}
                    disabled
                    className="text-xs bg-slate-50 font-mono cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={!isProfileDirty || savingProfile}
                  className="bg-teal-600 hover:bg-teal-700 text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="update-profile-btn"
                >
                  {savingProfile ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-3.5 w-3.5 mr-1.5" /> Update Profile
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
